import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { driverApi } from '../../config/driverApi';
import { 
  MapPin, 
  Phone, 
  Clock, 
  Navigation,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ClipboardList,
  Truck
} from 'lucide-react';

const DriverOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchOrders = async () => {
    try {
      const res = await driverApi.get(`/dashboard.php?action=orders&status=${filter}`);
      if (res.data.success) {
        setOrders(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    setUpdatingId(orderId);
    try {
      const res = await driverApi.put(`/orders.php?action=status`, {
        order_id: orderId,
        status,
      });

      if (res.data.success) {
        fetchOrders();
      } else {
        alert(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network error');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      out_for_delivery: '#00BCD4',
      ready: '#9C27B0',
      pending: '#FFC107',
      confirmed: '#2196F3',
      preparing: '#FF9800',
      assigned: '#FF9800',
      accepted: '#2196F3',
      picked_up: '#4CAF50',
      delivered: '#4CAF50',
      cancelled: '#F44336',
      rejected: '#F44336',
    };
    const color = colors[status] || '#999';
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: color + '20',
        color: color,
        textTransform: 'capitalize',
      }}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const getActionButtons = (order) => {
    const buttons = [];
    const status = order.status;

    if (status === 'out_for_delivery' || status === 'ready') {
      buttons.push(
        <button
          key="accept"
          onClick={() => updateStatus(order.order_id, 'accepted')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#2196F3',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Accept'}
        </button>
      );
    }

    if (status === 'accepted') {
      buttons.push(
        <button
          key="driving"
          onClick={() => updateStatus(order.order_id, 'driving_to_restaurant')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#FF9800',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Start Driving'}
        </button>
      );
    }

    if (status === 'driving_to_restaurant') {
      buttons.push(
        <button
          key="arrived_restaurant"
          onClick={() => updateStatus(order.order_id, 'arrived_at_restaurant')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#9C27B0',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Arrived at Rest.'}
        </button>
      );
    }

    if (status === 'arrived_at_restaurant') {
      buttons.push(
        <button
          key="picked_up"
          onClick={() => updateStatus(order.order_id, 'picked_up')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#4CAF50',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Picked Up'}
        </button>
      );
    }

    if (status === 'picked_up') {
      buttons.push(
        <button
          key="out_for_delivery"
          onClick={() => updateStatus(order.order_id, 'out_for_delivery')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#00BCD4',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Out for Delivery'}
        </button>
      );
    }

    if (status === 'out_for_delivery') {
      buttons.push(
        <button
          key="arriving"
          onClick={() => updateStatus(order.order_id, 'arriving_soon')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#FF9800',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Arriving Soon'}
        </button>
      );
    }

    if (status === 'arriving_soon') {
      buttons.push(
        <button
          key="arrived"
          onClick={() => updateStatus(order.order_id, 'arrived')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#9C27B0',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Arrived'}
        </button>
      );
    }

    if (status === 'arrived') {
      buttons.push(
        <button
          key="delivered"
          onClick={() => updateStatus(order.order_id, 'delivered')}
          disabled={updatingId === order.order_id}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: updatingId === order.order_id ? '#ccc' : '#4CAF50',
            color: '#fff',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: updatingId === order.order_id ? 'not-allowed' : 'pointer',
          }}
        >
          {updatingId === order.order_id ? 'Updating...' : 'Complete Delivery'}
        </button>
      );
    }

    buttons.push(
      <Link
        key="detail"
        to={`/driver/orders/${order.order_id}`}
        style={{
          flex: 1,
          padding: '10px',
          borderRadius: '8px',
          backgroundColor: '#f5f5f5',
          color: '#333',
          border: '1px solid #ddd',
          fontSize: '0.8rem',
          fontWeight: 600,
          textAlign: 'center',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
        }}
      >
        Details <ChevronRight size={14} />
      </Link>
    );

    return buttons;
  };

  const getGoogleMapsUrl = (address) => {
    if (!address) return '#';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid #f0f0f0',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: '#1A1A1A',
          marginBottom: '12px',
        }}>
          My Orders
        </h2>

        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '4px',
        }}>
          {['all', 'out_for_delivery', 'ready', 'delivered', 'cancelled'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #ddd',
                backgroundColor: filter === f ? '#D4AF37' : '#fff',
                color: filter === f ? '#fff' : '#666',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
              }}
            >
              {f.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      {orders.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#999',
        }}>
          <ClipboardList size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ fontSize: '1rem', marginBottom: '8px' }}>No orders found</p>
          <p style={{ fontSize: '0.85rem' }}>You're all caught up!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {orders.map((order) => (
            <div
              key={order.order_id}
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #EAEDF1',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
              }}>
                <div>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#1A1A1A',
                    margin: '0 0 4px',
                  }}>
                    Order #{order.order_id}
                  </h3>
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#666',
                    margin: 0,
                  }}>
                    {new Date(order.order_date).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                {getStatusBadge(order.status)}
              </div>

              <div style={{ marginBottom: '12px' }}>
                {/* Restaurant Pickup Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  marginBottom: '10px',
                  backgroundColor: '#FFF8E1',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #FFE082',
                }}>
                  <MapPin size={16} color="#FFB300" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <strong style={{ fontSize: '0.8rem', color: '#B78103', display: 'block' }}>Pickup: Syra Food Point & Grill</strong>
                    <span style={{ fontSize: '0.78rem', color: '#666' }}>Seguku, Entebbe Road, Kampala (Tel: +256 770 000 001)</span>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <MapPin size={16} color="#666" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#333',
                    margin: 0,
                    lineHeight: 1.4,
                  }}>
                    {order.delivery_address || 'No address provided'}
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <Phone size={16} color="#666" />
                  <span style={{ fontSize: '0.85rem', color: '#333' }}>
                    {order.customer_name || 'Customer'} - {order.customer_phone || 'N/A'}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <Clock size={16} color="#666" />
                  <span style={{ fontSize: '0.85rem', color: '#333' }}>
                    Total: UGX {Number(order.total_amount).toLocaleString()}
                  </span>
                </div>

                {order.driver_name && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                  }}>
                    <Truck size={16} color="#666" />
                    <span style={{ fontSize: '0.85rem', color: '#333' }}>
                      Driver: {order.driver_name} ({order.driver_phone})
                    </span>
                  </div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
              }}>
                {getActionButtons(order)}
              </div>

              {order.delivery_address && (
                <div style={{ marginTop: '12px' }}>
                  <a
                    href={getGoogleMapsUrl(order.delivery_address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#1565C0',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    <Navigation size={14} />
                    Open Navigation
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DriverOrders;
